import { IconName } from "admin-ui/lib/icons"
import { lazy, LazyExoticComponent } from "react"
import { RouteObject } from "react-router-dom"
import { EUserRole, EVendorType } from "types"
import { hasRoutePermission } from "utils/route-permissions"

// common
const ErrorPage = lazy(() => import("modules/error"))
const NotFoundPage = lazy(() => import("modules/404"))

// layout
const ProtectedLayout = lazy(() => import("libraries/layout/protected.layout"))
const PublicLayout = lazy(() => import("libraries/layout/public.layout"))
const MainLayout = lazy(() => import("libraries/layout/main.layout"))
const AuthLayout = lazy(() => import("libraries/layout/auth.layout"))

const AdminGroup = [EUserRole.Pom_Super_Admin]
const VendorAdminGroup = [EUserRole.Vendor_Admin]
const VendorSuperAdminGroup = [EUserRole.Vendor_Super_Admin]
const TypeBrandStoreGroup = [EVendorType.BrandStore]

const VendorGroup = [...VendorSuperAdminGroup, ...VendorAdminGroup]
const AdminVendorGroup = [
  ...AdminGroup,
  ...VendorSuperAdminGroup,
  ...VendorAdminGroup,
]

export interface IRoute
  extends Omit<RouteObject, "element" | "children" | "path" | "errorElement"> {
  path?: string
  element?: LazyExoticComponent<any>
  children?: IRoute[]
  errorElement?: LazyExoticComponent<any>

  // support to show in sidebar
  isProtected?: boolean
  inSidebar?: boolean
  icon?: IconName
  label?: string
  showWithRoles?: EUserRole[]
  hideWithVendorType?: EVendorType[]
  forceHide?: boolean
  showForBrandStoreIfChatEnabled?: boolean
}

export interface IMenuItem {
  icon?: IconName
  label?: string
  path?: string
  inSidebar?: boolean
  children: IMenuItem[]
}

export const RoutePath = {
  HomePage: "/",
  Login: "/login",
  ForgotPassword: "/forgot-password",
  Products: "/products",
  AddProducts: "/products/add",
  Categories: "/categories",
  AddCategory: "/categories/add",
  Customers: "/customers",
  Promotions: "/promotions",
  AddPromotion: "/promotions/add",
  TierPromotions: "/tier-promotions",
  AddTierPromotions: "/tier-promotions/add",
  AddCustomers: "/customers/add",
  SubUsers: "/customers/sub-users",
  AddSubUsers: "/customers/sub-users/add",
  CustomerGroups: "/customers/customer-groups",
  AddCustomerGroups: "/customers/customer-groups/add",
  HashTag: "#",
  Orders: "/orders",
  Administration: "/administration",
  Taxes: "/administration/taxes",
  AddTaxes: "/administration/taxes/add",
  Shippings: "/administration/shippings",
  AddShipping: "/administration/shippings/add",
  Pages: "/pages",
  AddPage: "/pages/add",
  AdminUser: "/admin-user",
  AddAdminUser: "/admin-user/add",
  Vendors: "/vendors",
  AddVendorUser: "/vendors/add",
  VendorSetting: "/vendors/setting",
  IntegrationLogs: "/integration-logs",
  Events: "/events",
  AddEvent: "/events/add",
  Posts: "/posts",
  AddPost: "/posts/add",
  Widgets: "/widgets",
  AddWidget: "/widgets/add",
  LiveCard: "/orders/live-cards",
  AddLiveCard: "/orders/live-cards/add",
  Sales: "/sales",
  AddSales: "/sales/add",
  SalesRep: "/sales",
  AddSalesRep: "/sales/rep/add",
  SalesProductGroup: "/sales/product-group",
  AddSalesProductGroup: "/sales/product-group/add",
  SalesCustomerGroup: "/sales/customer-group",
  AddSalesCustomerGroup: "/sales/customer-group/add",
  Articles: "/articles",
  AddArticle: "/articles/add",
}

const configRoutes: IRoute[] = [
  // protected
  {
    element: ProtectedLayout,
    isProtected: true,
    children: [
      {
        element: MainLayout,
        children: [
          {
            path: RoutePath.HomePage,
            errorElement: ErrorPage,
            element: lazy(() => import("modules/home")),
            // icon: "home",
            label: "Home",
            showWithRoles: AdminVendorGroup,
          },
          // orders
          {
            inSidebar: true,
            icon: "shopping-cart",
            label: "Orders",
            showWithRoles: AdminVendorGroup,
            hideWithVendorType: TypeBrandStoreGroup,
            children: [
              {
                path: RoutePath.Orders,
                errorElement: ErrorPage,
                showWithRoles: AdminVendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/orders/list")),
                inSidebar: true,
                label: "View orders",
              },
              {
                path: `${RoutePath.Orders}/:orderId`,
                showWithRoles: AdminVendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/orders/detail")),
              },

              // live cards
              {
                path: RoutePath.LiveCard,
                errorElement: ErrorPage,
                showWithRoles: AdminVendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/live-carts/list")),
                inSidebar: true,
                label: "Abandoned/Live carts",
              },
              {
                path: `${RoutePath.LiveCard}/:slug`,
                showWithRoles: AdminVendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/live-carts/detail")),
              },
            ],
          },
          // products
          {
            inSidebar: true,
            icon: "tag",
            label: "Products",
            showWithRoles: VendorGroup,
            hideWithVendorType: TypeBrandStoreGroup,
            children: [
              {
                path: RoutePath.Categories,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/categories/list")),
                inSidebar: true,
                label: "Categories",
              },
              {
                path: `${RoutePath.Categories}/:slug`,
                element: lazy(() => import("modules/categories/detail")),
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
              },

              // products
              {
                path: RoutePath.Products,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/products/list")),
                inSidebar: true,
                label: "Products",
              },
              {
                path: `${RoutePath.Products}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/products/detail")),
              },
            ],
          },
          // customer
          {
            inSidebar: true,
            icon: "users",
            label: "Customers",
            showWithRoles: AdminVendorGroup,
            hideWithVendorType: TypeBrandStoreGroup,
            children: [
              {
                path: RoutePath.Customers,
                errorElement: ErrorPage,
                showWithRoles: AdminVendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/customers/list")),
                inSidebar: true,
                label: "Customers",
              },
              {
                path: `${RoutePath.Customers}/:slug`,
                showWithRoles: AdminVendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/customers/detail")),
              },

              // sub user
              {
                path: RoutePath.SubUsers,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/sub-users/list")),
                inSidebar: true,
                label: "Sub users",
              },
              {
                path: `${RoutePath.SubUsers}/:slug`,
                showWithRoles: AdminGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/sub-users/detail")),
              },

              // customer group
              {
                path: RoutePath.CustomerGroups,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/customer-groups/list")),
                inSidebar: true,
                label: "Customer groups",
              },
              {
                path: `${RoutePath.CustomerGroups}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/customer-groups/detail")),
              },
            ],
          },
          // marketing
          {
            inSidebar: true,
            icon: "receipt-percent",
            label: "Marketing",
            showWithRoles: AdminVendorGroup,
            children: [
              // promotion
              {
                path: RoutePath.Promotions,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/promotion/list")),
                inSidebar: true,
                label: "Promotion",
              },
              {
                path: `${RoutePath.Promotions}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/promotion/detail")),
              },

              // tier promotion
              {
                path: RoutePath.TierPromotions,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/tier-promotion/list")),
                inSidebar: true,
                label: "Tier Promotion",
              },
              {
                path: `${RoutePath.TierPromotions}/:slug`,
                hideWithVendorType: TypeBrandStoreGroup,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/tier-promotion/detail")),
              },

              // pages
              {
                path: RoutePath.Pages,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/pages/list")),
                inSidebar: true,
                label: "Pages",
              },
              {
                path: `${RoutePath.Pages}/:slug`,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/pages/detail")),
              },
              // events
              {
                path: RoutePath.Events,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/events/list")),
                inSidebar: true,
                label: "Events",
              },
              {
                path: `${RoutePath.Events}/:slug`,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/events/detail")),
              },
              // Content Feed
              {
                path: RoutePath.Posts,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/posts/list")),
                inSidebar: true,
                label: "Content feed",
              },
              {
                path: `${RoutePath.Posts}/:slug`,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/posts/detail")),
              },
              // articles / articles
              {
                path: RoutePath.Articles,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/articles/list")),
                inSidebar: true,
                label: "Articles",
              },
              {
                path: `${RoutePath.Articles}/:slug`,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/articles/detail")),
              },
            ],
          },
          // Website
          {
            inSidebar: true,
            icon: "line-globe",
            label: "Website",
            showWithRoles: AdminGroup,
            children: [
              // widgets
              {
                path: RoutePath.Widgets,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/widgets/list")),
                inSidebar: true,
                label: "Widgets",
              },
              {
                path: `${RoutePath.Widgets}/:slug`,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/widgets/detail")),
              },
            ],
          },
          // administration
          {
            inSidebar: true,
            icon: "timeline-vertical",
            label: "Administration",
            showWithRoles: VendorGroup,
            hideWithVendorType: TypeBrandStoreGroup,
            children: [
              // taxes
              {
                path: RoutePath.Taxes,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/taxes/list")),
                inSidebar: true,
                label: "Taxes",
              },
              {
                path: `${RoutePath.Taxes}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/taxes/detail")),
              },

              // shipping methods
              {
                path: RoutePath.Shippings,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/shippings/list")),
                inSidebar: true,
                label: "Shippings",
              },
              {
                path: `${RoutePath.Shippings}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                element: lazy(() => import("modules/shippings/detail")),
              },
            ],
          },
          // vendor
          {
            inSidebar: true,
            icon: "buildings",
            label: "Vendors",
            showWithRoles: AdminVendorGroup,
            children: [
              // setting
              {
                path: RoutePath.VendorSetting,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/vendors/setting")),
                inSidebar: true,
                label: "Vendor setting",
              },

              // admin user
              {
                path: RoutePath.AdminUser,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/vendors/list")),
                inSidebar: true,
                label: "Admin user",
              },

              // admin user detail
              {
                path: `${RoutePath.AdminUser}/:slug`,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/vendors/detail")),
              },

              // vendor list
              {
                path: RoutePath.Vendors,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/vendors/list")),
                inSidebar: true,
                label: "Vendors",
              },

              // vendor detail
              {
                path: `${RoutePath.Vendors}/:slug`,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/vendors/detail")),
              },
            ],
          },
          // sales
          {
            inSidebar: true,
            icon: "chat-bubble-left-right",
            label: "Sales Representatives",
            showWithRoles: VendorGroup,
            hideWithVendorType: TypeBrandStoreGroup,
            showForBrandStoreIfChatEnabled: true,
            children: [
              // sale representative
              {
                path: RoutePath.Sales,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                element: lazy(() => import("modules/sales/sale-rep/list")),
                inSidebar: true,
                hideWithVendorType: TypeBrandStoreGroup,
                showForBrandStoreIfChatEnabled: true,
                label: "Sales Representative",
              },
              {
                path: `${RoutePath.Sales}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                showForBrandStoreIfChatEnabled: true,
                element: lazy(() => import("modules/sales/sale-rep/detail")),
              },

              // sale product group
              {
                path: RoutePath.SalesProductGroup,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                showForBrandStoreIfChatEnabled: true,
                element: lazy(
                  () => import("modules/sales/sale-product-group/list")
                ),
                inSidebar: true,
                label: "Sales Product group",
              },
              {
                path: `${RoutePath.SalesProductGroup}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                showForBrandStoreIfChatEnabled: true,
                element: lazy(
                  () => import("modules/sales/sale-product-group/detail")
                ),
              },

              // sale customer group
              {
                path: RoutePath.SalesCustomerGroup,
                errorElement: ErrorPage,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                showForBrandStoreIfChatEnabled: true,
                element: lazy(
                  () => import("modules/sales/sale-customer-group/list")
                ),
                inSidebar: true,
                label: "Sales Customer group",
              },
              {
                path: `${RoutePath.SalesCustomerGroup}/:slug`,
                showWithRoles: VendorGroup,
                hideWithVendorType: TypeBrandStoreGroup,
                showForBrandStoreIfChatEnabled: true,
                element: lazy(
                  () => import("modules/sales/sale-customer-group/detail")
                ),
              },
            ],
          },
          // integrations (BC customer sync)
          {
            inSidebar: true,
            icon: "link",
            label: "Integrations",
            showWithRoles: AdminGroup,
            children: [
              {
                path: RoutePath.IntegrationLogs,
                errorElement: ErrorPage,
                showWithRoles: AdminGroup,
                element: lazy(() => import("modules/integration-logs/list")),
                inSidebar: true,
                label: "BC Integration Logs",
              },
            ],
          },
        ],
      },
    ],
  },

  // public
  {
    element: PublicLayout,
    children: [
      {
        element: AuthLayout,
        errorElement: ErrorPage,
        children: [
          {
            path: RoutePath.Login,
            element: lazy(() => import("modules/login")),
          },
          {
            path: RoutePath.ForgotPassword,
            element: lazy(() => import("modules/forgot-password")),
          },
        ],
      },
      {
        path: "*",
        element: NotFoundPage,
      },
    ],
  },
]

export const getMenuList = (
  role?: EUserRole,
  vendorType?: EVendorType,
  isShowChat?: boolean
): IMenuItem[] => {
  const protectedRoutes = configRoutes.filter((item) => item.isProtected)
  const mainMenus = (protectedRoutes[0].children ?? [])[0].children
  if (!mainMenus) return []

  const getItems = (items: IRoute[]) => {
    const result: IMenuItem[] = []
    for (const item of items) {
      // Use the same permission logic as route protection
      const hasPermission = hasRoutePermission(item, {
        role,
        vendorType,
        isShowChat,
      })

      if (item.inSidebar && hasPermission) {
        result.push({
          icon: item.icon,
          label: item.label,
          path: item.path,
          children: getItems(item.children ?? []),
        })
      }
    }
    return result
  }

  return getItems(mainMenus)
}

export const sidebarMenus = getMenuList()

export default configRoutes
